/**
 * tableStyles — the canonical data table (Design System §4.2), as shared
 * inline-style objects so dashboards stop re-deriving the spec.
 *
 * Usage:
 *   <div style={tableStyles.wrap}>
 *     <table style={tableStyles.table}>
 *       <thead><tr><th style={tableStyles.th}>…</th></tr></thead>
 *       <tbody><tr><td style={tableStyles.td}>…</td>
 *                  <td style={tableStyles.tdNum}>1,234</td></tr></tbody>
 *     </table>
 *   </div>
 *
 * Row hover (--bg-surface-2) needs a pseudo-class — either reuse an existing
 * CSS hover rule or set onMouseEnter/Leave; sticky machinery stays in
 * Invoicing.css (the reference implementation).
 */

import React from 'react';

/** Container: white surface, hairline border, clipped corners. */
const wrap: React.CSSProperties = {
  background: 'var(--bg-surface-1)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-lg)',
  overflow: 'hidden',
};

/** Scrollable variant of the container for wide tables. */
const wrapScroll: React.CSSProperties = {
  ...wrap,
  overflowX: 'auto',
};

const table: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: 'var(--font-sans)',
};

/** Header cell: the micro-label lock (§2.2) on a neutral-50 band. */
const th: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  background: 'var(--cg-neutral-50)',
  padding: 12,
  borderBottom: '1px solid var(--border-1)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

/** Numeric header cell (right-aligned to match tdNum). */
const thNum: React.CSSProperties = {
  ...th,
  textAlign: 'right',
};

/** Body cell. */
const td: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-1)',
  padding: 12,
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};

/** Numeric body cell: right-aligned + tabular-nums, always (§2.2). */
const tdNum: React.CSSProperties = {
  ...td,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

/** Sticky-header variant of th for tables that scroll vertically. */
const thSticky: React.CSSProperties = {
  ...th,
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

/** Row background for the expanded/detail state. */
const trExpanded: React.CSSProperties = {
  background: 'var(--bg-surface-3)',
};

export const tableStyles = {
  wrap,
  wrapScroll,
  table,
  th,
  thNum,
  thSticky,
  td,
  tdNum,
  trExpanded,
  /** Row hover color — apply via CSS rule or mouse handlers. */
  rowHoverBackground: 'var(--bg-surface-2)' as const,
};

export default tableStyles;
